import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { sbQuery, SB_URL, SB_KEY } from "../config/supabase";

const SLPContext = createContext({
  slpRate: 10,
  setSLPRate: () => {},
  balance: 0,
  referralBalance: 0,
  refreshBalance: () => {},
  setCredentials: () => {},
});

export function useSLP() {
  return useContext(SLPContext);
}

/* Helpers exported for use anywhere without the hook */
export function slpToNgn(slp, rate = 10) {
  return Math.round(slp * rate);
}
export function ngnToSlp(ngn, rate = 10) {
  return Math.ceil(ngn / rate);
}
export function fmtSLP(slp) {
  return Number(slp).toLocaleString();
}

export function SLPProvider({ children }) {
  const [slpRate, setSlpRate] = useState(10);
  const [balance, setBalance] = useState(0);
  const [referralBalance, setReferralBalance] = useState(0);
  const tokenRef = useRef(null);
  const userIdRef = useRef(null);

  // Fetch platform rate on mount
  useEffect(() => {
    fetch(`${SB_URL}/rest/v1/platform_settings?key=eq.slp_rate&select=value`, {
      headers: { apikey: SB_KEY, "Content-Type": "application/json" },
    })
      .then(r => { if (!r.ok) return []; return r.json(); })
      .then(rows => {
        if (Array.isArray(rows) && rows[0]?.value) setSlpRate(Number(rows[0].value) || 10);
      })
      .catch(() => {});
  }, []);

  // Fetch balance from DB
  const refreshBalance = useCallback(() => {
    const t = tokenRef.current;
    const u = userIdRef.current;
    if (!t || !u) return Promise.resolve(0);
    return sbQuery("slp_balances", t, "GET", null, `?user_id=eq.${u}&select=balance,referral_balance`)
      .then(rows => {
        const b = rows?.[0]?.balance || 0;
        const rb = rows?.[0]?.referral_balance || 0;
        setBalance(b);
        setReferralBalance(rb);
        return b;
      })
      .catch(() => 0);
  }, []);

  // Set credentials and immediately fetch balance
  const setCredentials = useCallback((token, userId) => {
    tokenRef.current = token;
    userIdRef.current = userId;
    if (token && userId) {
      refreshBalance();
    } else {
      setBalance(0);
    }
  }, [refreshBalance]);

  return (
    <SLPContext.Provider value={{ slpRate, setSLPRate: setSlpRate, balance, setBalance, referralBalance, setReferralBalance, refreshBalance, setCredentials }}>
      {children}
    </SLPContext.Provider>
  );
}
